import {compact} from '@benchling/js-utils';
import {Events, Model, View} from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';

import {AllPolymerLayouts} from 'constants/MapsConstants';
import Component, {ComponentOptions} from 'molbio/maps/linear/Component';
import {Layout} from 'molbio/maps/linear/LinearLayout';
import BenchlingProfiler, {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {POLYMER_LAYOUT_TO_PROFILER_EVENTS} from 'performance/processMolbioEvents';
import {ProfilerEvent} from 'performance/ProfilerConstants';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {VisibilityOption} from 'settings/visibility-options/VisibilityOptions';
import throttle from 'util/throttle';

export interface BaseReactWrapperViewOptions<LayoutName extends keyof AllPolymerLayouts>
  extends ComponentOptions {
  className: string;
  polymerLayoutName: LayoutName;
  disableMinimize?: boolean;
  secondarySettings?: Array<string>;
  bottomMarginBleed?: () => number;
  shouldUpdateForAdjust?: boolean;
  additionalListeners?: Array<{
    other: Events;
    event: string;
    throttleWait?: number;
  }>;
}

/**
 * A component that helps render a React component as part of a SliceView.
 *
 * @param options.className className to put on the view's el, should match the component's key in SliceView's
 *   `componentMethods`.
 * @param options.polymerLayoutName The key into model.get('polymerLayout') which renderReact may depend on.
 * @param options.disableMinimize For alignments, allows the view to not hide when visibility option
 *   vo.MAXIMISE is turned off.
 * @param options.bottomMarginBleed A function that takes no arguments and returns an integer representing
 *   the minimum amount of margin the component must have at its bottom edge (measured in pixels)
 * @param options.shouldUpdateForAdjust Whether this component cares about changes to the position of the
 *   bases layer
 * @param options.additionalListeners Optional additional listeners to trigger an update for e.g. handling
 *   feature selection
 * @param options.visibilityOptions The visibility options available for this map
 */
export default abstract class BaseReactWrapperView<
  LayoutName extends keyof AllPolymerLayouts,
> extends Component {
  layout!: Layout;
  polymerLayoutName!: LayoutName;
  secondaryOptionNames!: Array<string>;
  secondaryOptions!: Array<VisibilityOption>;
  bottomMarginBleed?: () => number;
  shouldUpdateForAdjust!: boolean;
  additionalListeners!: Array<{
    other: Events;
    event: string;
    throttleWait?: number;
  }>;

  override initialize(options: BaseReactWrapperViewOptions<LayoutName>): void {
    super.initialize(options);
    this.polymerLayoutName = options.polymerLayoutName;
    this.className = options.className;
    if (options.disableMinimize) {
      this.maximiseOption = null;
    }

    const visibilityOptions = options.visibilityOptions ?? this.model.get('visibilityOptions');
    this.secondaryOptionNames = options.secondarySettings || [];
    this.secondaryOptions = compact(
      this.secondaryOptionNames.map((name) => visibilityOptions.getOption(name))
    );

    this.bottomMarginBleed = options.bottomMarginBleed;
    this.shouldUpdateForAdjust = options.shouldUpdateForAdjust ?? false;
    this.redraw = this.redraw.bind(this);
    this.additionalListeners = options.additionalListeners ?? [];
  }

  override registerListeners(): void {
    super.registerListeners();

    this.secondaryOptions.forEach((option) => {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.listenTo(option, 'change:visible', this.update);
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.listenTo(option.get('settings'), 'change:value', this.update);
    });

    // As a performance optimization, this component only re-renders when its specific polymerLayout
    // subobject changes. We could rewrite this to rely on having renderReact be fast and render a
    // PureRenderMixin component.
    this.listenTo(this.model, 'change:polymerLayout', () => {
      const oldLayout = this.model.previous('polymerLayout')[this.polymerLayoutName];
      const newLayout = this.model.get('polymerLayout')[this.polymerLayoutName];
      if (oldLayout !== newLayout) {
        this.update();
      }
    });

    for (const {other, event, throttleWait} of this.additionalListeners) {
      let updateCallback: () => void = this.update.bind(this);
      if (throttleWait != null) {
        // If a numeric throttleWait is specified for the listener, throttle the update callback.
        updateCallback = throttle(this.update.bind(this), throttleWait);
      }
      this.listenTo(other, event, updateCallback);
    }
  }

  abstract redraw(newHeight?: number | ((layout: AllPolymerLayouts[LayoutName]) => number)): void;

  /**
   * Renders the actual React slice. Generally, children should call this.reactRender with appropriate
   * arguments.
   */
  abstract _renderComponentWrapper(): JSX.Element;

  /**
   * Performs render profiling and calls a function overridden by children to render the actual React slice.
   * Should NOT be overridden.
   */
  renderComponentWrapper(): void {
    // As a performance optimization, we avoid rendering this component if it won't be shown. Note that we
    // don't need to unmount the component since it will be hidden with CSS via Component.showHandler.
    if (this.shouldShow()) {
      let startEvent: ProfilerEvent | null = null;
      let endEvent: ProfilerEvent | null = null;
      if (this.name != null) {
        const profilerEvents = POLYMER_LAYOUT_TO_PROFILER_EVENTS[this.name];
        startEvent = profilerEvents.startEvent;
        endEvent = profilerEvents.endEvent;
      }
      if (startEvent != null) {
        logProfilerTimingEvent(startEvent);
      }
      renderToDOM(
        endEvent != null ? (
          <BenchlingProfiler {...endEvent}>{this._renderComponentWrapper()}</BenchlingProfiler>
        ) : (
          this._renderComponentWrapper()
        ),
        this.el
      );
    }
  }

  getLayout(): AllPolymerLayouts[LayoutName] {
    return this.model.get('polymerLayout')[this.polymerLayoutName];
  }

  override setupComponentWrapper(): void {
    // Override the default d3-rendering of component wrapper in favor of using React
    this.renderComponentWrapper();
  }

  update(trigger: boolean = true): void {
    this.renderComponentWrapper();
    if (trigger) {
      this.trigger('redraw');
    }
  }

  updateForAdjust(): void {
    if (this.shouldUpdateForAdjust) {
      this.update(false);
    }
  }

  override remove(): View<Model> {
    this.die();
    return super.remove();
  }

  override die(): void {
    super.die();
    ReactDOM.unmountComponentAtNode(this.el);
  }
}
